<script setup>
import {onMounted, ref, watch} from 'vue'
    import SignatureRequests from './signatureRequests.vue'
    import ExpertContractPreferences from './preferences.vue'
    import Workflows from './workflows.vue'
    import ContentEditor from './contentEditor.vue'
    import Messages from './messages.vue'
    import { useNotificationStore } from '@/stores/NotificationStore.js'

    const notificationStore = useNotificationStore()
    const props = defineProps({
        expertContractId: {
            type: Number,
            required: true
        }
    })

    const navigationOptions = [
        {
            label: _mft('expertContract:navigation.overview'),
            name: 'content',
        },
        {
            label: _mft('expertContract:navigation.preferences'),
            name: 'preferences',
        },
        {
            label: _mft('expertContract:navigation.workflows'),
            name: 'workflows',
        },
        {
            label: _mft('expertContract:navigation.recipients'),
            name: 'recipients',
        },
    ]

    const preferencescomponent = ref(null)
    const messagescomponent = ref(null)
    const signaturerequestscomponent = ref(null)
    const loaded = ref(false)

    const modal = ref(null)
    const workflows = ref([])
    const workflowLogs = ref([])
    const savingTitleStatus = ref('')
    const selectedWorkflow = ref(null)
    const currentSection = ref(navigationOptions[0])
    const expertContract = ref(null)
    const conditions = ref({})
    const preferences = ref(false)
    const editorType = ref(null)

    const awaitingPdfState = ref('')
    const creatingInvoiceState = ref('')

    const loadExpertContract = async () => {
        const response = await axios.get(route('expert-contracts.edit', props.expertContractId))
        expertContract.value = response.data.expert_contract
        conditions.value = {...conditions.value, ...response.data.conditions}
        loaded.value = true
    }

    const loadWorkflows = async () => {
        const response = await axios.get(route('expert-contracts.workflows.index'))
        workflows.value = response.data
    }

    const updateSelectedWorkflow = (workflow) => {
        selectedWorkflow.value = workflow
    }

    const performRefresh = () => {
        preferencescomponent.value?.reload()
        messagescomponent.value?.reload()
    }

    const setSection = (section) => {
        currentSection.value = section

        if (section.name === 'preferences') {
            preferencescomponent.value.reload()
        }
        if (section.name === 'recipients') {
            signaturerequestscomponent.value.reload()
        }
    }

    const updateTitle = async () => {
        savingTitleStatus.value = 'waiting'
        const res = await axios.post(route('expert-contract.updateTitle', props.expertContractId), {
            title: expertContract.value.title
        })

        if (res.data.success === true) {
            savingTitleStatus.value = 'success'
        } else {
            savingTitleStatus.value = 'error'
        }
    }

    const loadPreferences = async () => {
        const response = await axios.get(route('expert-contract-preferences.show', props.expertContractId))
        preferences.value = response.data.preferences
        conditions.value = {...conditions.value, ...response.data.conditions}
    }

    const updatePreferences = (newPreferences) => {
        preferences.value = newPreferences
    }

    const updateEditorType = (newEditorType) => {
        editorType.value = newEditorType
    }

    const onSigningStatusChanged = (newStatus) => {
        let notificationContent = ''

        if (newStatus.signing_enabled) {
            notificationContent = _mft('expertContract:preferences.signingStatuses.enabled')
        } else {
            notificationContent = _mft('expertContract:preferences.signingStatuses.disabled')
        }

        notificationStore.addNotification({
            type: 'success',
            title: _mft('expertContract:preferences.signingStatuses.statusUpdated'),
            message: notificationContent,
            dismissAfter: 3000,
        })
    }

    const requestPdfCopy = async () => {
        awaitingPdfState.value = 'waiting'
        const res = await axios.get(route('expert-contracts.createPdf', props.expertContractId))

        if (res.data.success) {
            awaitingPdfState.value = 'success'
        } else {
            awaitingPdfState.value = 'error'
        }
    }

    const createInvoice = async () => {
        creatingInvoiceState.value = 'waiting'
        try {
            const res = await axios.post(route('expert-contracts.create-invoice', props.expertContractId))

            if (res.data.message) {
                creatingInvoiceState.value = 'success'
                notificationStore.addNotification({
                    type: 'success',
                    title: _mft('expertContract:invoice.created'),
                    message: res.data.message,
                    dismissAfter: 5000,
                })
            } else {
                creatingInvoiceState.value = 'error'
                notificationStore.addNotification({
                    type: 'error',
                    title: _mft('expertContract:invoice.error'),
                    message: res.data.error || 'Failed to create invoice',
                    dismissAfter: 5000,
                })
            }
        } catch (error) {
            creatingInvoiceState.value = 'error'
            const errorMessage = error.response?.data?.error || error.message || 'Failed to create invoice'
            notificationStore.addNotification({
                type: 'error',
                title: _mft('expertContract:invoice.error'),
                message: errorMessage,
                dismissAfter: 5000,
            })
        }
    }

    onMounted(() => {
        loadExpertContract()
        loadWorkflows()
        loadPreferences()
    })

</script>
<template>
    <mf-spinner-medium v-if="! loaded" class="w-12 h-6"></mf-spinner-medium>
    <div v-else>
        <frosted-bar
            :breadcrumbs="[
                    {url: route('expert-contracts.index'), label: _mft('navigation:backOffice.sidebar.toolbox.contracts.label')},
                    {url: '', label: expertContract.title},
                ]"
        >
            <div class="flex flex-row items-center justify-between">

                <h1 class="text-lg leading-4 font-semibold">
                    {{ expertContract.title }}
                </h1>

                <div class="flex flex-row items-center space-x-2">
                    <p
                        v-if="preferences.signing_enabled || conditions.has_signed_signature_requests"
                        class="text-xs text-gray-400 mb-0"
                    >
                        <span
                            v-if="props.signingEnabled && !conditions.has_signed_signature_requests"
                        >
                            {{ _mft('expertContract:editingDisabledDueToSigningEnabled') }}
                        </span>
                        <span
                            v-if="conditions.has_signed_signature_requests"
                        >
                            <template v-if="conditions.all_signature_requests_signed">
                                {{ _mft('expertContract:editingDisabledDueToAllSigned') }}
                            </template>
                            <template v-else>
                                {{ _mft('expertContract:editingDisabledDueToSigningInProgress') }}
                            </template>
                        </span>

                        {{ _mft('expertContract:editingDisabledDueToSigningEnabled') }}
                    </p>

                    <a
                        v-if="!preferences.signing_enabled && !conditions.has_signed_signature_requests && editorType === 'closer'"
                        :href="route('nina.quote.edit', expertContract.id)"
                    >
                        <button-primary>
                            {{ _mft('expertContract:contentEditor.editWithAi') }}
                        </button-primary>
                    </a>
                    <a
                        v-else-if="!preferences.signing_enabled && !conditions.has_signed_signature_requests && editorType === 'page-builder'"
                        :href="route('expert-contracts.pagebuilder', expertContract.id)"
                    >
                        <button-primary>
                            {{ _mft('expertContract:contentEditor.editWithPageBuilder') }}
                        </button-primary>
                    </a>
                    <expert-contract-delete :expert-contract="expertContract">
                        <template #trigger="{ open }">
                            <button-more-options
                                alignment="right"
                                :cancel-on-outside-click="false"
                                options-class="min-w-36"
                            >
                                <button-more-options-option
                                    class="whitespace-nowrap"
                                    style-type="danger"
                                    @click="open"
                                >
                                    {{ _mft('expertContract:deleteContract') }}
                                </button-more-options-option>
                            </button-more-options>
                        </template>
                    </expert-contract-delete>
                    <button-success
                        @click="updateTitle"
                        :state="savingTitleStatus"
                    >
                        {{ _mft('shared:action.save') }}
                    </button-success>
                </div>
            </div>
        </frosted-bar>

        <div
            v-if="conditions.has_signature_requests && conditions.all_signature_requests_signed"
            class="mb-2 -mt-2 flex justify-end space-x-2"
        >
            <button-secondary
                size="sm"
                :state="awaitingPdfState"
                :disabled="awaitingPdfState === 'waiting'"
                @click="requestPdfCopy"
            >
                {{ _mft('expertContract:downloadCopyAsPdf') }}
            </button-secondary>
            <button-success
                v-if="conditions.has_unimicro_integration"
                size="sm"
                :state="creatingInvoiceState"
                :disabled="creatingInvoiceState === 'waiting'"
                @click="createInvoice"
            >
                {{ _mft('expertContract:createInvoice') }}
            </button-success>
        </div>

        <ui-tabbed
            :track-state="true"
            :display-in-tab-bar="true"
        >
            <ui-tabbed-tab
                :title="_mft('expertContract:navigation.overview')"
                value="content"
                :active="true"
            >
                <div class="flex flex-col-reverse xl:grid xl:grid-cols-4 xl:gap-4">
                    <card-default class="col-span-3">
                        <div :class="preferences.messages_enabled ? 'flex space-x-8 items-center' : ''">
                            <div
                                v-if="! preferences.signing_enabled"
                                class="flex items-center space-x-2 w-full"
                            >
                                <input-text
                                    name="title"
                                    :label="_mft('expertContract:title')"
                                    class="w-full"
                                    v-model:content="expertContract.title"
                                    required
                                ></input-text>
                            </div>
                            <div
                                v-else
                                class="flex justify-between items-center w-full"
                            >
                                <h2>
                                    {{ expertContract.title }}
                                </h2>
                                <Messages
                                    ref="messagescomponent"
                                    :messaging-enabled="preferences.messages_enabled"
                                    :expert-contract-id="props.expertContractId"
                                />
                            </div>
                        </div>

                        <div class="-mx-4">
                            <ContentEditor
                                v-if="preferences"
                                :expert-contract="expertContract"
                                :conditions="conditions"
                                :signing-enabled="preferences.signing_enabled"
                                @editor-type-loaded="updateEditorType"
                            />
                            <mf-spinner-medium v-else class="w-12 h-6"></mf-spinner-medium>
                        </div>
                    </card-default>
                    <div class="mb-4">
                        <ExpertContractPreferences
                            ref="preferencescomponent"
                            :expert-contract-id="props.expertContractId"
                            @preferencesUpdated="updatePreferences"
                            @signing-status-changed="onSigningStatusChanged"
                        />
                    </div>
                </div>
            </ui-tabbed-tab>
            <ui-tabbed-tab
                :title="_mft('expertContract:navigation.workflows')"
                value="workflows"
            >
                <div>
                    <Workflows
                        :current-workflow-id="expertContract.workflow_id"
                        :expert-contract-id="props.expertContractId"
                        @workflowChanged="updateSelectedWorkflow"
                    />
                </div>
            </ui-tabbed-tab>
            <ui-tabbed-tab
                :title="_mft('expertContract:navigation.recipients')"
                value="recipients"
            >
                <div>
                    <SignatureRequests
                        ref="signaturerequestscomponent"
                        :expert-contract-id="props.expertContractId"
                        :selected-workflow="selectedWorkflow"
                        @preferencesUpdated="updatePreferences"
                        @doRefresh="performRefresh"
                    />
                </div>
            </ui-tabbed-tab>
        </ui-tabbed>
    </div>
</template>
